// ─── Layout Utilities ───────────────────────────────────────────────────────────

// Default layout — used when the user has no saved layout
export const DEFAULT_LAYOUT = [
    { i: "projects", x: 0, y: 0, w: 3, h: 1, minW: 1, minH: 1, maxH: 1 },
];

// Column configuration
export const COLS = { lg: 3, md: 3, sm: 1, xs: 1, xxs: 1 };
export const LG_COLS = 3;

// Row height in pixels — each widget is exactly 1 row tall.
// This equals the old rowHeight(60) * h(5) + margin(20) * (h-1)(4) = 380px.
export const ROW_HEIGHT = 380;

// Height unit used in backend storage (old format)
const STORED_H = 5;

// Migrate layouts saved under the old 12-column grid to the new 3-column grid,
// and normalise all items to h=1 row units.
export function migrateLayoutTo3Col(savedLayout) {
    // Detect old 12-col layouts: any item with w > 3 or x > 2
    const isOld = savedLayout.some((item) => item.w > 3 || item.x > 2);

    return savedLayout.map((item) => {
        const base = isOld
            ? {
                  ...item,
                  w: Math.max(1, Math.min(3, Math.round(item.w / 4))),
                  x: Math.max(0, Math.min(2, Math.round(item.x / 4))),
                  minW: 1,
              }
            : { ...item };

        // Convert y from old h-unit scale to h=1 scale
        if (base.h > 1) {
            base.y = Math.round(base.y / base.h);
        }
        base.h = 1;
        base.minH = 1;
        base.maxH = 1;

        return base;
    });
}

// Convert 3-col positions back to 12-col for API storage.
// Also restores h/y to the old h=5 scale so existing backend data stays consistent.
export function convertLayoutForStorage(layoutToSave) {
    return layoutToSave.map((item) => ({
        ...item,
        w: item.w * 4,
        x: item.x * 4,
        h: STORED_H,
        y: item.y * STORED_H,
        minW: (item.minW || 1) * 4,
    }));
}

// Repack a layout so widgets fill available space (used on add and delete).
// Uses a greedy column-packing algorithm: for each widget (preserving order),
// find the lowest y position across all valid x placements.
// Pass { presorted: true } to skip internal sort (e.g. after a manual reorder).
export function repackLayout(
    layout,
    cols = LG_COLS,
    { presorted = false } = {},
) {
    const sorted = presorted
        ? [...layout]
        : [...layout].sort((a, b) => a.y - b.y || a.x - b.x);

    // Track the height each column extends to
    const colHeights = new Array(cols).fill(0);

    return sorted.map((item) => {
        const w = Math.min(item.w, cols); // clamp to grid width

        // Find the x position that yields the lowest y
        let bestY = Infinity;
        let bestX = 0;
        for (let x = 0; x <= cols - w; x++) {
            const y = Math.max(...colHeights.slice(x, x + w));
            if (y < bestY) {
                bestY = y;
                bestX = x;
            }
        }

        // Update column heights
        for (let c = bestX; c < bestX + w; c++) {
            colHeights[c] = bestY + item.h;
        }

        return { ...item, x: bestX, y: bestY };
    });
}

// Generate stacked layouts for smaller breakpoints from the lg layout
export function computeAllLayouts(layout) {
    const result = { lg: layout };
    for (const bp of ["md", "sm", "xs", "xxs"]) {
        const cols = COLS[bp];
        if (cols >= LG_COLS) {
            // Same column count as lg — reuse the lg layout as-is
            result[bp] = layout;
        } else {
            // Fewer columns — stack all items at full width
            let y = 0;
            result[bp] = layout.map((item) => {
                const entry = { ...item, x: 0, y, w: cols };
                y += item.h;
                return entry;
            });
        }
    }
    return result;
}
